import React from 'react';
import { isEmpty, find } from 'lodash';
import { Button, Icon } from 'antd';
import DataPageGrid from './DataPageGrid';
import DataPageAdder from './DataPageAdder';
import { BULK_DELETE_DATA_CONFIRMATION } from '../../constants/text.constants';
import { ConditionalPopConfirm } from './index';
import { StyledIcon, ModalDeleteWrapper } from './styles';

// This is used after bulk edit the data page.
const reGetAfterUpdate = (get, queryParameters, modal, updateModalContent) => get({
  queryParameters,
  successCallback: ({ results }) => {
    const updatedRow = results
      .filter(row => modal.data.content.map(({ itemId }) => itemId).includes(row.itemId));
    return updateModalContent(updatedRow);
  },
});

// This is used after edit nested items.
const reGetAfterUpdateNested = (get, queryParameters, modal, updateModalContentForNested) => get({
  queryParameters,
  successCallback: ({ results }) => {
    // The initial does not have a row data, quit early.
    if (!modal || !modal.data || !modal.data.row) return false;
    // The itemId is used in here due to its nature is different than generic id.
    // The itemId represents original the id that is linked with active plan.
    // Usually an id is changed when unlinking from active plan, not itemId.
    // This applies to rows in all data type except nested items.
    // ItemId would change with id in the nested item.
    const updatedRow = find(results, {
      itemId: modal.data.row.itemId,
    });
    const childName = modal.data.name;
    return updateModalContentForNested(updatedRow, childName);
  },
});

const DataPageModal = ({
  modal, itemLookUps, gridApi, loadingQueue, meta, queryParameters, onCopyPreviousWeek,
  onBulkEdit, getMeta, updateModalContent, updateModalContentForNested,
  addModal, checkedState, parentNotEditable, onSelect, onBulkDeleteNested,
}) => {
  const checkedStateArray = Object.values(checkedState);
  const noItemsSelected = checkedStateArray.length === 0;
  // editLockDown applies to data edit in row-level in a data type.
  // isNotEditable applies to the entire data type.
  const { editLockDown, bulkEditing } = modal;

  const modalTypeNotEditable = !getMeta(modal.data.type).currentMeta.editable
  const isNotEditable = parentNotEditable || modalTypeNotEditable
  return (
    <>
      {(!bulkEditing && !isNotEditable && !editLockDown) && (
        <ModalDeleteWrapper>
          <ConditionalPopConfirm
            placement="topLeft"
            icon={<StyledIcon type="exclamation-circle" color="red" />}
            popTitle={BULK_DELETE_DATA_CONFIRMATION(checkedState)}
            okText="Delete data"
            okType="danger"
            onConfirm={() => onBulkDeleteNested(queryParameters, modal, () => {
              reGetAfterUpdateNested(
                gridApi.get,
                queryParameters,
                modal,
                updateModalContentForNested,
              );
            })}
            disabled={isNotEditable || noItemsSelected}
          >
            <Button disabled={isNotEditable || noItemsSelected}>
              <Icon
                type="delete"
                theme={(isNotEditable || noItemsSelected) ? 'outlined' : 'twoTone'}
                twoToneColor="#f00"
              />
              Delete Selected
            </Button>
          </ConditionalPopConfirm>
        </ModalDeleteWrapper>
      )}
      <DataPageGrid
        rows={modal.data.content}
        meta={getMeta(modal.data.type)}
        lookups={itemLookUps.data}
        isNotEditable={isNotEditable || editLockDown}
        gridApi={{
          ...gridApi,
          get: () => reGetAfterUpdateNested(
            gridApi.get,
            queryParameters,
            modal,
            updateModalContentForNested,
          ),
        }}
        bulkEditing={modal.bulkEditing}
        loading={!isEmpty(loadingQueue)}
        onBulkEdit={(edit, success) => onBulkEdit(edit, () => {
          success();
          reGetAfterUpdate(
            gridApi.get,
            queryParameters,
            modal,
            updateModalContent,
          );
        })}
        addModal={addModal}
        checkedState={checkedState}
        onSelect={onSelect}
      />
      {!isNotEditable && !modal.bulkEditing
        && (
          <DataPageAdder
            toCompare={modal.data.content}
            meta={find(meta.childMeta, {
              currentMeta: { item_type: modal.data.type },
            })}
            parent={{
              id: modal.data.row.id,
              name: meta.currentMeta.item_type,
            }}
            api={{
              get: () => reGetAfterUpdateNested(
                gridApi.get,
                queryParameters,
                modal,
                updateModalContentForNested,
              ),
              post: gridApi.post,
            }}
            lookups={itemLookUps.data}
          />
        )
      }
    </>
  );
};

export default DataPageModal;
